/**
 * Forecast areas: the one public region vocabulary (spec 2026-10-01, section 4.2).
 *
 * Every active condition location belongs to exactly one leaf area (`tier: "area"`); a leaf may
 * have one parent (`tier: "region"`). The catalogue is generated into
 * `catalog.generated.ts` by `scripts/generate-forecast-areas.ts` and mirrored into the
 * `forecast_areas` table by the generated seed migration. Pure types, safe everywhere.
 */

export type AreaKind = "coastal" | "mountain";
export type AreaTier = "area" | "region";
export type AreaScope = "local" | "country";
export type AreaPageSport = "surf" | "piste";
export type MemberType = "surf" | "ski" | "sailing";
export type SurfDifficulty = "beginner" | "intermediate" | "advanced" | "expert";

/** Board sports the area readers accept. Named SUP profiles are never accepted. */
export type AreaBoardSport =
  | "piste" | "off-piste" | "touring" | "mtb"
  | "surf" | "bodyboarding" | "wind" | "foil" | "downwind-foil" | "wing-foil"
  | "diving" | "swimming" | "sup" | "sailing";

export const AREA_BOARD_SPORTS: readonly AreaBoardSport[] = Object.freeze([
  "piste", "off-piste", "touring", "mtb",
  "surf", "bodyboarding", "wind", "foil", "downwind-foil", "wing-foil",
  "diving", "swimming", "sup", "sailing",
]);

/** Pure: true only for the generic board sports (false for sup-touring, sup-surf, sup-downwind). */
export function isAreaBoardSport(value: string): value is AreaBoardSport {
  return (AREA_BOARD_SPORTS as readonly string[]).includes(value);
}

export type AreaSection = Readonly<{ key: string; name: string }>;

export type ForecastArea = Readonly<{
  slug: string;
  name: string;
  kind: AreaKind;
  tier: AreaTier;
  scope: AreaScope;
  countryCodes: readonly string[];
  parent: string | null;
  /** Derived: areas whose parent is this slug. */
  children: readonly string[];
  sections: readonly AreaSection[];
  pageSports: readonly AreaPageSport[];
  /** Catalogue intent; see isAreaEffectivelyIndexable. */
  indexable: boolean;
  neighbours: readonly string[];
  centroid: Readonly<{ lat: number; lon: number }>;
  timezone: string;
  aliases: readonly string[];
  intro: string | null;
  introFacts: readonly string[];
  introSources: readonly string[];
  /** Derived, generator rule 9. */
  jobsHref: string;
}>;

export type ForecastAreaMember = Readonly<{
  slug: string;
  /** Leaf area slug. */
  area: string;
  section: string | null;
  locality: string | null;
  /** DB name at generation time. */
  name: string;
  /** Override, else "{name}, {locality}" unless the name already contains the locality. */
  displayName: string;
  type: MemberType;
  countryCode: string;
  lat: number;
  lon: number;
  timezone: string;
  difficulty: SurfDifficulty | null;
  beginnerFriendly: boolean;
  facingDeg: number | null;
  spotType: string | null;
  glacier: boolean;
  plannerDomains: readonly string[];
  /**
   * Addition to the spec shape: the board sports this member is published on and curated
   * eligible for (sportEligibility() over the snapshot facts), for "at least 4 eligible members"
   * rules (board chips, nearest area) without a database read.
   */
  boardSports: readonly AreaBoardSport[];
  /** Addition: typical season window from the catalogue snapshot ("MM-DD"), ski members only. */
  seasonStart: string | null;
  seasonEnd: string | null;
}>;

export type ForecastAreaCatalog = Readonly<{
  /** sha256 of the normalised sources, first 12 hex. */
  version: string;
  generatedAtISO: string;
  areas: readonly ForecastArea[];
  members: readonly ForecastAreaMember[];
}>;

/** One entry of the slim, edge-safe route index. */
export type AreaRouteIndexEntry = Readonly<{ pageSports: readonly AreaPageSport[]; parent: string | null }>;
